// Signed-out landing page. Editorial treatment: typography-first hero with
// a real decklist specimen, bento value-props with size hierarchy, vertical
// step timeline, flat mono cost sheet, narrow "Notes" section for Q&A.
// Accent is used once per viewport. Mana notation appears in running copy
// as the product-native idiom (not fantasy set dressing).

import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import { ArrowRight } from "lucide-react";
import { ACTION_COSTS, PLANS } from "../lib/credits";
import { cn } from "../lib/cn";
import LiquidButton from "../components/ui/LiquidButton";
import { useSeoHead } from "../lib/useSeoHead";

// Fallback used both on the SSR/initial paint and when /api/stats is
// unreachable. Sourced from the most recent ingest snapshot — kept here
// as a constant so we never render "—" or a 0 to a crawler.
const FALLBACK_EXEMPLAR_COUNT = 1048;

// Live-fetch the indexed-tournament-list count so the marquee number on
// the landing page tracks the actual corpus instead of going stale every
// time we re-ingest. Anonymous endpoint, cached one minute by the browser
// via the JSON response shape — no auth concerns.
function useExemplarCount(): number {
  const [count, setCount] = useState<number>(FALLBACK_EXEMPLAR_COUNT);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/stats")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: unknown) => {
        if (cancelled || !data || typeof data !== "object") return;
        const decks = (data as { decks?: { count?: number } }).decks;
        if (decks && typeof decks.count === "number" && decks.count > 0) {
          setCount(decks.count);
        }
      })
      .catch(() => {
        // Stay on FALLBACK_EXEMPLAR_COUNT — no UI churn for a stats blip.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return count;
}

const LANDING_TITLE =
  "MTG Oracle: AI deck builder + retrieval stack for MTG";
const LANDING_DESCRIPTION =
  "Build Magic: The Gathering Standard decks from cards you own. Import your collection, explore upgrades to buy, and playtest your deck in Forge.";

export default function Landing() {
  useSeoHead({
    title: LANDING_TITLE,
    description: LANDING_DESCRIPTION,
    canonical: "https://themtgoracle.com/",
  });
  const exemplarCount = useExemplarCount();
  // Promo-dismissed state lives here (not inside PromoStrip) so the Header
  // can drop its sticky top-offset back to 0 when the strip is gone —
  // otherwise the header keeps a ~40px gap above it and the top looks broken.
  const [promoDismissed, setPromoDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("promo:planeswalker:dismissed") === "1";
    } catch {
      return false;
    }
  });
  const dismissPromo = () => {
    try {
      localStorage.setItem("promo:planeswalker:dismissed", "1");
    } catch {
      // ignore
    }
    setPromoDismissed(true);
  };
  return (
    <div className="min-h-screen bg-bg-base text-fg">
      {!promoDismissed && <PromoStrip onDismiss={dismissPromo} />}
      <Header promoVisible={!promoDismissed} />
      <main>
        <Hero exemplarCount={exemplarCount} />
        <CollectionFeature />
        <ByTheNumbers exemplarCount={exemplarCount} />
        <ValueProps exemplarCount={exemplarCount} />
        <UnderTheHood />
        <DeckBuildingManifesto />
        <HowItWorks />
        <Pricing />
        <Notes exemplarCount={exemplarCount} />
        <CtaFooter />
      </main>
      <Footer />
    </div>
  );
}

// Sticky promo strip. Sits above the Header with an animated rainbow
// flow background so it catches the eye on every viewport while the user
// scrolls. Code is rendered as a high-contrast chip in the bar surface.
// Drives sign-up; redeemed at Polar checkout. Dismissable so repeat
// visitors aren't nagged; persists in localStorage.
const PROMO_RAINBOW = [
  "rgba(231,77,255,0.85)",
  "rgba(0,149,255,0.85)",
  "rgba(131,255,166,0.75)",
  "rgba(255,180,0,0.85)",
  "rgba(255,0,80,0.85)",
];

function PromoStrip({ onDismiss }: { onDismiss: () => void }) {
  const stops = [...PROMO_RAINBOW, PROMO_RAINBOW[0]]
    .map((color, i) => `${color} ${(i * 50) / PROMO_RAINBOW.length}%`)
    .join(", ");
  return (
    <div className="sticky top-0 z-50 w-full overflow-hidden bg-bg-base shadow-md">
      <div
        aria-hidden
        className="absolute inset-0 z-0 promo-flow"
        style={{
          backgroundImage: `repeating-linear-gradient(70deg, ${stops})`,
          backgroundSize: "200% 100%",
          filter: "saturate(1.6)",
          maskImage:
            "linear-gradient(to bottom, white, transparent), radial-gradient(circle at top center, white, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, white, transparent), radial-gradient(circle at top center, white, transparent)",
          maskComposite: "intersect",
          WebkitMaskComposite: "source-in",
        }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-4 md:px-6 h-10 md:h-11 flex items-center justify-center gap-2 md:gap-3 text-xs md:text-sm font-medium text-fg">
        <span className="hidden sm:inline-flex font-mono text-[10px] uppercase tracking-widest text-fg/80">
          Beta
        </span>
        <span className="hidden sm:inline-block h-3.5 w-px bg-fg/30" />
        <span className="truncate">
          <span className="font-semibold">10% off</span>
          <span className="hidden sm:inline"> all plans &amp; top-ups</span>
          <span className="text-fg/80"> with code</span>
        </span>
        <code className="font-mono tracking-tight bg-bg-base text-fg rounded px-2 py-0.5 text-[11px] md:text-xs font-semibold ring-1 ring-fg/30 shadow-sm">
          planeswalker
        </code>
        <SignUpButton mode="modal">
          <button className="hidden md:inline-flex items-center gap-1 rounded bg-fg text-bg-base hover:bg-fg/90 transition-colors px-2.5 py-1 text-[11px] font-semibold tracking-wide uppercase shadow-sm">
            Claim
            <ArrowRight size={12} strokeWidth={2.5} />
          </button>
        </SignUpButton>
        <button
          aria-label="Dismiss promo"
          onClick={onDismiss}
          className="ml-1 md:ml-2 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-fg/10 transition-colors text-fg/70"
        >
          <span aria-hidden className="text-base leading-none">×</span>
        </button>
      </div>
      <style>{`
        @keyframes promo-flow {
          from { background-position: 0% 0; }
          to { background-position: 100% 0; }
        }
        .promo-flow {
          animation: promo-flow 20s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .promo-flow { animation: none; }
        }
      `}</style>
    </div>
  );
}

function Header({ promoVisible }: { promoVisible: boolean }) {
  // Sticky header that stays visually transparent over the hero, then
  // gains a translucent backdrop + hairline border once the user scrolls
  // past ~120px (well past the H1 fold, before the numbers band). Pure
  // scroll listener — no IntersectionObserver target needed since the
  // threshold is fixed. The sticky top-offset matches the promo strip's
  // height when it's present, and collapses to 0 once it's dismissed.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky z-40 transition-colors duration-200",
        promoVisible ? "top-9 md:top-10" : "top-0",
        scrolled
          ? "bg-bg-base/80 backdrop-blur border-b border-border/60"
          : "bg-transparent border-b border-transparent",
      )}
    >
      <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5">
          <img src="/logo.svg" alt="MTG Oracle" width={26} height={26} />
          <span className="font-semibold tracking-tight">MTG Oracle</span>
        </a>
        <nav className="flex items-center gap-1">
          <a
            href="#features"
            className="hidden sm:inline-flex btn-ghost text-sm"
          >
            Features
          </a>
          <a
            href="#pricing"
            className="hidden sm:inline-flex btn-ghost text-sm"
          >
            Pricing
          </a>
          <SignInButton mode="modal">
            <button className="btn-ghost">Sign in</button>
          </SignInButton>
          <SignUpButton mode="modal">
            <button className="btn-primary">Try it free</button>
          </SignUpButton>
        </nav>
      </div>
    </header>
  );
}

function Hero({ exemplarCount }: { exemplarCount: number }) {
  // Round to a friendlier "1,000+" style headline — exact corpus size
  // shows in ByTheNumbers below for readers who care about the precise
  // figure.
  const heroFloor = useMemo(() => {
    const rounded = Math.max(1000, Math.floor(exemplarCount / 1000) * 1000);
    return `${rounded.toLocaleString()}+`;
  }, [exemplarCount]);
  return (
    <section className="relative grain overflow-hidden">
      <div aria-hidden className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="relative mx-auto max-w-4xl px-6 pt-14 pb-20 md:pt-24 md:pb-28">
        <h1
          className="font-display text-6xl md:text-[5.5rem] font-medium tracking-[-0.02em] leading-[0.95] anim-rise"
          style={{ animationDelay: "40ms" }}
        >
          Name a win condition.
          <br />
          <span className="foil">Build the deck to pull it off.</span>
        </h1>
        <p
          className="mt-8 text-lg text-fg-muted max-w-xl leading-relaxed anim-rise"
          style={{ animationDelay: "220ms" }}
        >
          Tell the Oracle what you want your deck to do.
          It drafts a 60-card{" "}
          <strong className="text-fg font-medium">Magic: The Gathering</strong>{" "}
          list grounded in {heroFloor} real tournament decks, scores it against
          the live meta, and runs play tests in Forge before you ever sleeve a card.
          Import your collection to build with the cards and copies you already own.
        </p>
        <div
          className="mt-9 anim-rise"
          style={{ animationDelay: "380ms" }}
        >
          <SignUpButton mode="modal">
            <LiquidButton
              className="text-base h-12"
              trailing={<ArrowRight size={16} strokeWidth={2.2} />}
            >
              Build your first deck
            </LiquidButton>
          </SignUpButton>
        </div>
        <p
          className="mt-4 text-xs text-fg-subtle anim-rise"
          style={{ animationDelay: "480ms" }}
        >
          Start free with 100 credits/month. Paid plans from $10/month. Standard-legal cards only.
        </p>
      </div>
    </section>
  );
}

// Collection building is a primary entry point, immediately after the hero.
function CollectionFeature() {
  return (
    <section id="collections" className="border-t border-border/60 bg-bg-surface/30 scroll-mt-24">
      <div className="mx-auto max-w-6xl px-6 py-16 md:py-20 grid gap-10 md:grid-cols-2 md:gap-16">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-accent">
            Build from your collection
          </div>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.05]">
            Your cards. Your next deck.
          </h2>
          <p className="mt-5 text-fg-muted leading-relaxed max-w-lg">
            Find a new use for the cards in your binder or Arena account.
            Import your collection, choose it when you build, and give the
            Oracle a plan. It builds around the cards you own and respects
            how many copies you have, with basic lands always available.
          </p>
          <SignUpButton mode="modal">
            <button className="btn-primary mt-6 inline-flex items-center gap-2">
              Build with my cards <ArrowRight size={16} />
            </button>
          </SignUpButton>
        </div>
        <ol className="divide-y divide-border/60">
          {[
            {
              title: "Bring your collection.",
              body: "Upload a CSV from Untapped.gg, ManaBox, Moxfield, Archidekt, Deckbox, or Dragon Shield. You can also paste a cardlist.",
            },
            {
              title: "Build something you can play.",
              body: "Select your collection in the builder. Get a Standard deck built around your available cards and copies, plus an explanation of how it wins.",
            },
            {
              title: "See what to get next.",
              body: 'Keep suggestions within your collection, or switch to “All cards” in the deck viewer to explore upgrades and extra copies to buy. Your original collection stays recorded.',
            },
          ].map((step, i) => (
            <li key={step.title} className="flex gap-4 py-5 first:pt-0 last:pb-0">
              <span className="font-mono text-xs text-fg-subtle pt-1" aria-hidden>
                0{i + 1}
              </span>
              <div>
                <h3 className="text-lg font-semibold tracking-tight">{step.title}</h3>
                <p className="mt-2 text-sm text-fg-muted leading-relaxed">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// A mono-numerals band after the collection feature. Four hero figures at
// editorial scale. No icons, no chrome. This is "numerals as typographic
// object" — the most MTG-native visual idiom we have. The exemplar
// count is sourced from /api/stats so the marquee number never goes
// stale — the other figures are stable invariants.
function ByTheNumbers({ exemplarCount }: { exemplarCount: number }) {
  const numbers: Array<{ figure: string; label: string }> = [
    {
      figure: exemplarCount.toLocaleString(),
      label: "tournament lists indexed",
    },
    { figure: "4,430", label: "all cards in Standard indexed and embedded" },
    { figure: "< 60s", label: "prompt to playtest-ready" },
    { figure: "∞", label: "countless combos" },
  ];
  return (
    <section className="border-y border-border/60 bg-bg-surface/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
          {numbers.map((n) => (
            <div key={n.label}>
              <div className="font-mono tabular-nums text-3xl md:text-4xl font-medium text-fg leading-none">
                {n.figure}
              </div>
              <div className="mt-3 text-xs text-fg-subtle leading-snug max-w-[18ch]">
                {n.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Bento grid grouped into six editorial themes — Build, Iterate, Playtest,
// Compete, Share, Limited. Each theme rolls up several shipped capabilities
// rather than spelling them out as a feature laundry list. The flagship
// (Compete) keeps its real-readout specimen; the rest carry a single
// product idiom (a prompt, a mini W-L-D, a version timeline, a chip list)
// so the page reads like a magazine rather than a spec sheet.
function ValueProps({ exemplarCount }: { exemplarCount: number }) {
  return (
    <section
      id="features"
      className="scroll-mt-20"
    >
      <div className="mx-auto max-w-6xl px-6 py-24 md:py-28">
        <div className="font-mono text-[10px] uppercase tracking-widest text-accent">
          Features
        </div>
        <h2 className="mt-3 font-display text-4xl md:text-6xl font-medium tracking-[-0.02em] leading-[1.0] max-w-3xl">
          Cites its sources.
          <br />
          <span className="foil">Plays its games.</span>
          <br />
          Tracks the meta.
          <br />
          Versions your iterations.
        </h2>
        <p className="mt-6 max-w-2xl text-fg-muted leading-relaxed">
          Six things the Oracle does that a chatbot can't: ground every pick
          in real tournament data, iterate with you turn-by-turn, run the
          simulated games, prove its lists against the meta, share builds
          publicly, and draft a Limited pool from a phone photo. Start from your
          collection to put the cards you already own to work.
        </p>

        {/* 6-col bento. Compete (flagship) spans 4 cols and 2 rows. The
            other five fill 2/2 + 3/3 patterns so the grid doesn't template-
            feel. Order: Compete (flagship), Build, Iterate, Playtest,
            Share, Limited. */}
        <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-6 md:auto-rows-[minmax(0,auto)]">
          {/* COMPETE — flagship: tournament-proximity scoring + meta gauntlet */}
          <article className="surface min-w-0 p-6 md:p-8 md:col-span-4 md:row-span-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
              Compete · flagship
            </div>
            <h3 className="mt-3 font-display text-3xl md:text-4xl font-medium tracking-[-0.02em] leading-[1.05]">
              Scored against the meta. Played against it.
            </h3>
            <p className="mt-3 text-fg-muted leading-relaxed max-w-lg">
              Every build is scored against the nearest competitive
              exemplars on card overlap, mana base, and core-card presence,
              then run through the Gauntlet against the top-3 meta
              archetypes head-to-head. You see at a glance whether your
              brew is one swap away from a tier-1 list, or a mile off.
            </p>

            {/* Real readout specimen — mono, tabular, bordered. The score
                itself is the only accent in this viewport. */}
            <div className="mt-6 rounded-md border border-border bg-bg-base/60 p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-4">
                <span className="text-[11px] uppercase tracking-widest text-fg-subtle">
                  meta score
                </span>
                <span className="font-mono text-xs text-fg-subtle tabular-nums">
                  vs. MTGTop8 top-32
                </span>
              </div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="font-mono tabular-nums text-5xl md:text-6xl font-medium text-accent">
                  82
                </span>
                <span className="font-mono tabular-nums text-fg-subtle">
                  /100
                </span>
              </div>
              <div className="mt-4 h-1 w-full rounded-full bg-border/60 overflow-hidden">
                <div className="h-full bg-accent" style={{ width: "82%" }} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4 font-mono text-[11px] tabular-nums text-fg-muted">
                <div>
                  <div className="text-fg-subtle">overlap</div>
                  <div className="text-fg">86%</div>
                </div>
                <div>
                  <div className="text-fg-subtle">mana</div>
                  <div className="text-fg">A+</div>
                </div>
                <div>
                  <div className="text-fg-subtle">cores</div>
                  <div className="text-fg">14/16</div>
                </div>
              </div>

              {/* Mega Build / Gauntlet rollup — chip list of the six top-meta
                  matchups your build runs against. */}
              <div className="mt-5 pt-4 border-t border-border/60">
                <div className="text-[11px] uppercase tracking-widest text-fg-subtle">
                  gauntlet · top-3 meta
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 font-mono text-[11px]">
                  <span className="rounded border border-border bg-bg-base/60 px-2 py-1 tabular-nums">
                    Boros Aggro <span className="text-fg-subtle">3-2</span>
                  </span>
                  <span className="rounded border border-border bg-bg-base/60 px-2 py-1 tabular-nums">
                    Izzet Lessons <span className="text-fg-subtle">2-3</span>
                  </span>
                  <span className="rounded border border-border bg-bg-base/60 px-2 py-1 tabular-nums">
                    Dimir Midrange <span className="text-fg-subtle">4-1</span>
                  </span>
                </div>
              </div>
            </div>
          </article>

          {/* BUILD — natural language + multimodal + grounded retrieval */}
          <article className="surface p-6 md:col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
              Build
            </div>
            <h3 className="mt-2 text-lg font-semibold tracking-tight">
              Prompt, paste, or photograph
            </h3>
            <p className="mt-2 text-sm text-fg-muted leading-relaxed">
              Type a brief, paste a decklist, or drop a phone photo of cards.
              Select an imported collection to use your owned cards and copies.
              The Oracle grounds every pick in{" "}
              {exemplarCount.toLocaleString()} tournament lists.
            </p>
            <pre className="mt-4 rounded-md border border-border bg-bg-base/60 p-3 font-mono text-xs text-fg overflow-hidden">
              <span className="text-fg-subtle">{"› "}</span>
              build a dimir midrange under $150
              {"\n"}
              <span className="text-fg-subtle">  + image: my-collection.jpg</span>
            </pre>
          </article>

          {/* ITERATE — threaded Q&A + Apply Ask + version history */}
          <article className="surface p-6 md:col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
              Iterate
            </div>
            <h3 className="mt-2 text-lg font-semibold tracking-tight">
              Ask. Apply. Revert.
            </h3>
            <p className="mt-2 text-sm text-fg-muted leading-relaxed">
              Every deck is a thread. Ask follow-ups, apply suggestions
              straight into the editor, and roll back to any prior version
              when an experiment doesn't pan out.
            </p>
            <div className="mt-4 flex items-center gap-2 font-mono text-[11px] tabular-nums">
              <span className="rounded border border-accent/60 bg-bg-base/60 px-2 py-1 text-accent">
                v3
              </span>
              <span className="text-fg-subtle">←</span>
              <span className="rounded border border-border bg-bg-base/60 px-2 py-1 text-fg-muted">
                v2
              </span>
              <span className="text-fg-subtle">←</span>
              <span className="rounded border border-border bg-bg-base/60 px-2 py-1 text-fg-muted">
                v1
              </span>
            </div>
          </article>

          {/* CARD SEARCH — semantic vector retrieval */}
          <article className="surface p-6 md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
              Search
            </div>
            <h3 className="mt-2 text-lg font-semibold tracking-tight">
              Semantic card search, built-in
            </h3>
            <p className="mt-2 text-sm text-fg-muted leading-relaxed">
              Every legal card is embedded into a high-dimensional vector
              index. Ask by meaning, not just keywords.
            </p>
            <pre className="mt-4 rounded-md border border-border bg-bg-base/60 p-3 font-mono text-xs text-fg overflow-hidden">
              <span className="text-fg-subtle">{"› "}</span>
              cheap counterspells with upside
              {"\n"}
              <span className="text-fg-subtle">  → 12 results · top: </span>
              Three Steps Ahead <span className="text-fg-subtle">({"{1}{U}"})</span>
            </pre>
          </article>

          {/* SHARE — public decks, fork, OG images */}
          <article className="surface p-6 md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
              Share
            </div>
            <h3 className="mt-2 text-lg font-semibold tracking-tight">
              Publish, link, fork
            </h3>
            <p className="mt-2 text-sm text-fg-muted leading-relaxed">
              Toggle any deck public and you get a clean shareable URL with a
              custom unfurl card. Anyone can read the strategy and Q&A;
              signed-in players can fork it into their own account in one
              click and start iterating.
            </p>
            <pre className="mt-4 rounded-md border border-border bg-bg-base/60 p-3 font-mono text-xs text-fg overflow-hidden">
              <span className="text-fg-subtle">themtgoracle.com/decks/</span>
              you/dimir-midrange-150
            </pre>
          </article>

          {/* LIMITED — draft builder from uploaded pool */}
          <article className="surface p-6 md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
              Limited
            </div>
            <h3 className="mt-2 text-lg font-semibold tracking-tight">
              Sealed and Draft, from a phone photo
            </h3>
            <p className="mt-2 text-sm text-fg-muted leading-relaxed">
              Snap your sealed pool or draft picks. Vision parses the cards,
              the Oracle drafts a 40-card list with a curve and a sideboard
              plan, and a Limited-aware validator catches every legality
              edge case before you submit.
            </p>
          </article>

          {/* PLAYTEST — Forge sim + match summaries + mana curve charts */}
          <article className="surface p-6 md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
              Playtest
            </div>
            <h3 className="mt-2 text-lg font-semibold tracking-tight">
              Played, not theorized.
            </h3>
            <p className="mt-2 text-sm text-fg-muted leading-relaxed">
              Every deck runs head-to-head in Forge, the open-source
              rules-complete MTG engine, with per-game logs, mana-curve
              and life-total charts, and a natural-language match recap.
            </p>
            <div className="mt-4 flex items-center gap-3 font-mono text-xs tabular-nums">
              <span className="rounded border border-border bg-bg-base/60 px-2 py-1">
                <span className="text-fg-subtle">W</span>{" "}
                <span className="text-fg">7</span>
              </span>
              <span className="rounded border border-border bg-bg-base/60 px-2 py-1">
                <span className="text-fg-subtle">L</span>{" "}
                <span className="text-fg">3</span>
              </span>
              <span className="rounded border border-border bg-bg-base/60 px-2 py-1">
                <span className="text-fg-subtle">D</span>{" "}
                <span className="text-fg">0</span>
              </span>
              <span className="text-fg-subtle">turn-to-win 8.4</span>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

// Technical credibility section. Prose-first, no fabricated diagrams — the
// real technical depth lives at /technical-faq. This section's job is to
// signal "there's real engineering here" and hand off to the deep dive.
function UnderTheHood() {
  const stack = [
    { k: "corpus", v: "DuckDB + LanceDB (structured + vector)" },
    { k: "retrieval", v: "BM25 + Cohere Embed v4, fused via RRF" },
    { k: "reranking", v: "Cohere Rerank 3.5 cross-encoder" },
    { k: "models", v: "Claude Opus 4.7 · Sonnet 4.6 · Haiku 4.5" },
    { k: "runtime", v: "AWS Bedrock, versioned prompts, A/B evals" },
  ];
  return (
    <section className="border-t border-border/60">
      <div className="mx-auto max-w-4xl px-6 py-24 md:py-28">
        <div className="font-mono text-[10px] uppercase tracking-widest text-accent">
          Under the hood
        </div>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.05]">
          A retrieval stack, <span className="foil">not a chatbot.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-fg-muted leading-relaxed">
          The Oracle runs on a purpose-built retrieval-augmented agent
          pipeline. Two specialised stores, hybrid lexical + semantic
          search, reciprocal-rank-fusion, a cross-encoder reranker, and a
          multi-model Claude ensemble gated by versioned prompts and an A/B
          eval harness.
        </p>

        {/* Flat spec list — reads like a whitepaper colophon, not a diagram. */}
        <dl className="mt-10 border-y border-border/60 divide-y divide-border/60 font-mono text-sm">
          {stack.map((row) => (
            <div
              key={row.k}
              className="grid grid-cols-[8rem_1fr] gap-4 py-3"
            >
              <dt className="text-fg-subtle uppercase tracking-widest text-[11px] pt-0.5">
                {row.k}
              </dt>
              <dd className="text-fg tabular-nums">{row.v}</dd>
            </div>
          ))}
        </dl>

        <Link
          to="/technical-faq"
          className="mt-8 inline-flex items-baseline gap-1 text-accent hover:underline font-medium"
        >
          Read the full technical deep dive →
        </Link>
      </div>
    </section>
  );
}

// The deck-building guide card. Sister to UnderTheHood — that
// section signals "real engineering"; this one signals "real Magic
// reasoning." Crawler-discoverable summary of the named pro principles
// the agent follows, with a deep link into the full /deck-building-method
// page. Mirrors the UnderTheHood layout (eyebrow → display headline →
// prose → flat list → CTA) on purpose so the two technical-credibility
// blocks read as a pair.
const PRO_PRINCIPLES = [
  {
    rule: "Misassignment of Role = Game Loss",
    pro: "Mike Flores",
  },
  {
    rule: "Every card needs a job; reactive cards must lead to winning",
    pro: "Reid Duke",
  },
  {
    rule: "Potential vs realized power; commit to a single role",
    pro: "PVDDR",
  },
  {
    rule: "Mana-base regression — colored sources are not negotiable",
    pro: "Frank Karsten",
  },
  {
    rule: "UBER vs CABS — pick one and commit",
    pro: "Limited Resources",
  },
  {
    rule: "A kill plan that survives sideboarding, every match",
    pro: "Pro Tour sideboarding doctrine",
  },
] as const;

function DeckBuildingManifesto() {
  return (
    <section
      id="deck-building-method"
      className="border-t border-border/60 bg-bg-surface/30 scroll-mt-20"
    >
      <div className="mx-auto max-w-4xl px-6 py-24 md:py-28">
        <div className="font-mono text-[10px] uppercase tracking-widest text-accent">
          The deck-building guide
        </div>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.05]">
          Constrained by named pros, <span className="foil">not guesswork.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-fg-muted leading-relaxed">
          The agent's system prompt encodes a small set of named pro
          principles — Flores, Reid Duke, PVDDR, Frank Karsten, Limited
          Resources — and rejects its own draft when a slot violates one.
          Every rule traces back to a named source, and every rule was
          added because removing it produced measurably worse decks in
          our eval harness. This is the guide, in short form.
        </p>

        <dl className="mt-10 border-y border-border/60 divide-y divide-border/60 font-mono text-sm">
          {PRO_PRINCIPLES.map((row) => (
            <div
              key={row.rule}
              className="grid grid-cols-[1fr_10rem] gap-4 py-3"
            >
              <dt className="text-fg leading-snug">{row.rule}</dt>
              <dd className="text-fg-subtle text-right tabular-nums">
                {row.pro}
              </dd>
            </div>
          ))}
        </dl>

        <Link
          to="/deck-building-method"
          className="mt-8 inline-flex items-baseline gap-1 text-accent hover:underline font-medium"
        >
          Read the full deck-building guide →
        </Link>
      </div>
    </section>
  );
}

// Vertical timeline replaces the 3-column "01/02/03" grid. Verb-first step
// labels, accent dot on the connector, mono step-index in the gutter only
// (not as a giant ornament).
const STEPS = [
  {
    verb: "Describe.",
    body: "A favorite card, a budget, a theme, a matchup you're trying to beat. Choose an imported collection to build with cards you own.",
  },
  {
    verb: "Retrieve.",
    body: "The Oracle, grounded on tournament exemplars, drafts a 60-card list with sideboard and explains each pick along with win conditions, mana curve analysis, and sideboard strategy.",
  },
  {
    verb: "Playtest.",
    body: "See how close it sits to the meta. Run simulated matches against your other decks. Get a detailed summary of weaknesses and strengths from the dozens of games in minutes. Iterate until it wins.",
  },
];

function HowItWorks() {
  return (
    <section className="border-t border-border/60 bg-bg-surface/30">
      <div className="mx-auto max-w-3xl px-6 py-24">
        <div className="font-mono text-[10px] uppercase tracking-widest text-accent">
          How it works
        </div>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.05]">
          From prompt to playtest-ready, in under a minute.
        </h2>
        <ol className="relative mt-12 border-l border-border/70 ml-2">
          {STEPS.map((s, i) => (
            <li key={s.verb} className="relative pl-8 pb-10 last:pb-0">
              <span className="absolute -left-[5px] top-1.5 block h-2.5 w-2.5 rounded-full bg-accent ring-4 ring-bg-surface" />
              <div className="font-mono text-[11px] tabular-nums text-fg-subtle">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-1 text-xl font-semibold tracking-tight">
                {s.verb}
              </h3>
              <p className="mt-2 text-fg-muted leading-relaxed max-w-prose">
                {s.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Pricing() {
  const billedActions = [
    { label: "Build a deck", cost: ACTION_COSTS.build_deck, hint: "60-card list + sideboard, grounded in tournament data" },
    { label: "Upload & analyze", cost: ACTION_COSTS.upload_deck, hint: "Parse, validate, and LLM-analyze your own list" },
    { label: "Ask about a deck", cost: ACTION_COSTS.ask_deck, hint: "Conversational follow-up with card lookups" },
    { label: "Match summary", cost: ACTION_COSTS.match_summary, hint: "LLM recap of a completed match" },
    { label: "Playtest match", cost: ACTION_COSTS.playtest, hint: "Forge-simulated games. Always free, run as many as you want" },
  ];
  const tiers: Array<typeof PLANS.free & { tagline: string; highlight?: boolean }> = [
    { ...PLANS.free, tagline: "Get started" },
    { ...PLANS.starter, tagline: "Casual" },
    { ...PLANS.pro, tagline: "Most popular", highlight: true },
    { ...PLANS.max, tagline: "Best value" },
  ];
  return (
    <section
      id="pricing"
      className="scroll-mt-20"
    >
      <div className="mx-auto max-w-5xl px-6 py-24">
        <div className="font-mono text-[10px] uppercase tracking-widest text-accent">
          Pricing
        </div>
        <h2 className="mt-3 font-display text-4xl md:text-6xl font-medium tracking-[-0.02em] leading-[1.0]">
          <span className="foil">Mana</span> untaps each upkeep.
        </h2>

        {/* Free-tier CTA strip. Single 1px accent border, no card, no
            gradient. Headline reads as a sentence; the button is the last
            clause. Mirrors the editorial register of CtaFooter. */}
        <div className="mt-10 flex flex-col gap-4 border border-accent/60 bg-bg-surface/40 rounded-md p-5 md:p-6 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-accent">
              Free for everyone
            </div>
            <p className="mt-2 text-base md:text-lg text-fg leading-snug">
              Every account starts with{" "}
              <span className="font-mono tabular-nums text-fg font-medium">
                {PLANS.free.monthly_grant}
              </span>{" "}
              credits a month, refreshed monthly.
              That is enough for{" "}
              <span className="font-mono tabular-nums text-fg font-medium">
                {Math.floor(PLANS.free.monthly_grant / ACTION_COSTS.build_deck)}
              </span>{" "}
              full deck builds and Q&A, plus unlimited free playtesting.
              No credit card needed.
            </p>
          </div>
          <SignUpButton mode="modal">
            <LiquidButton
              className="text-sm h-11 shrink-0"
              trailing={<ArrowRight size={15} strokeWidth={2.2} />}
            >
              Start free
            </LiquidButton>
          </SignUpButton>
        </div>

        {/* Flat mono cost sheet — reads like a price list, not a SaaS widget.
            No bordered grid cells; just tabular rows with a divider. */}
        <div className="mt-12 border-y border-border/60">
          <div className="px-1 py-3 text-[10px] uppercase tracking-widest text-fg-subtle border-b border-border/60">
            What each action costs
          </div>
          <ul className="divide-y divide-border/60 font-mono text-sm">
            {billedActions.map((a) => (
              <li
                key={a.label}
                className="flex items-baseline gap-4 px-1 py-3"
              >
                <span className="text-fg">{a.label}</span>
                <span
                  aria-hidden
                  className="flex-1 self-end border-b border-dotted border-border/60 translate-y-[-4px]"
                />
                <span
                  className={cn(
                    "tabular-nums",
                    a.cost === 0 ? "text-fg-muted" : "text-fg",
                  )}
                >
                  {a.cost === 0 ? "free" : `${a.cost} cr`}
                </span>
                <span className="hidden md:block w-[44ch] text-[11px] text-fg-subtle font-sans truncate">
                  {a.hint}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-16 grid gap-10 md:grid-cols-12">
          {/* Plans: four tiers in one horizontal strip. Popular tier has
              a 1px accent border and a tiny "popular" eyebrow — not a pill
              with a sparkle icon. */}
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-widest text-fg-subtle">
              Monthly plans
            </div>
            <div className="mt-4 grid gap-3 grid-cols-2 lg:grid-cols-4">
              {tiers.map((t) => (
                <div
                  key={t.id}
                  className={cn(
                    "rounded-md border p-4 bg-bg-surface/60 relative",
                    t.highlight
                      ? "border-accent/60"
                      : "border-border/70",
                  )}
                >
                  {t.highlight && (
                    <div className="absolute -top-2 left-4 bg-bg-base px-2 font-mono text-[9px] uppercase tracking-widest text-accent">
                      popular
                    </div>
                  )}
                  <div
                    className={cn(
                      "text-sm font-semibold",
                      t.id === "free" && "text-accent",
                    )}
                  >
                    {t.name}
                  </div>
                  <div className="text-[11px] text-fg-subtle mb-3">{t.tagline}</div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-mono tabular-nums text-xl text-fg">
                      {t.price_usd === null ? "$0" : `$${t.price_usd}`}
                    </span>
                    <span className="text-xs text-fg-subtle">/mo</span>
                  </div>
                  <div className="mt-1 text-xs text-fg-muted">
                    <span className="font-mono tabular-nums text-fg">
                      {t.monthly_grant.toLocaleString()}
                    </span>{" "}
                    credits/mo
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top-ups: stripped to three mono lines. Never expire. */}
          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-widest text-fg-subtle">
              One-time top-ups
            </div>
            <ul className="mt-4 divide-y divide-border/60 font-mono text-sm">
              {TOPUPS.map((t) => (
                <li
                  key={t.label}
                  className="flex items-baseline gap-3 py-3"
                >
                  <span className={cn("text-fg", t.best && "text-accent")}>
                    {t.label}
                  </span>
                  <span className="tabular-nums text-fg-subtle">
                    ${t.price}
                  </span>
                  <span className="flex-1 border-b border-dotted border-border/60 translate-y-[-4px]" />
                  <span className="tabular-nums text-fg">
                    {t.credits.toLocaleString()} cr
                  </span>
                  {t.best && (
                    <span className="text-[10px] uppercase tracking-widest text-accent font-sans">
                      +25%
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] text-fg-subtle leading-relaxed">
              Top-ups never expire. No subscription required.
              Failed actions refund automatically.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TOPUPS = [
  { label: "Small", price: 5, credits: 200, best: false },
  { label: "Medium", price: 15, credits: 700, best: false },
  { label: "Large", price: 40, credits: 2000, best: true },
] as const;

// Renamed from "Frequently asked questions" to "Notes" — an editorial
// framing rather than help-center framing. The technical-deep-dive Q has
// been moved to the top since it's the most mtg-agent-specific signal on
// the page. First two are open by default; the rest collapse.
const NOTES: ReadonlyArray<{ q: string; a: React.ReactNode; open?: boolean }> = [
  {
    q: "Can I build a deck from cards I already own?",
    open: true,
    a: "Yes. Import a collection CSV from Untapped.gg, ManaBox, Moxfield, Archidekt, Deckbox, or Dragon Shield, or paste a cardlist. Select that collection when building: the Oracle uses your available cards and owned quantities, with basic lands always available. Later, switch suggestions to All cards in the deck viewer to explore upgrades you may want to buy. The collection used for the original build stays recorded.",
  },
  {
    q: "Okay, but technically what is going on under the hood?",
    open: true,
    a: (
      <p>
        A retrieval-augmented agent stack on AWS Bedrock. A two-store corpus
        (DuckDB for structured filters, LanceDB for vectors), hybrid BM25 +
        Cohere Embed v4 retrieval fused by Reciprocal Rank Fusion, a Cohere
        Rerank 3.5 cross-encoder pass, and a multi-model Claude pipeline
        (Opus 4.7 / Sonnet 4.6 / Haiku 4.5) gated by versioned prompts and
        an A/B eval harness.{" "}
        <Link to="/technical-faq" className="text-accent hover:underline">
          Read the full technical deep dive →
        </Link>
      </p>
    ),
  },
  {
    q: "How does the agent actually reason about deck-building?",
    open: true,
    a: (
      <p>
        The system prompt encodes named pro principles: Mike Flores's
        &ldquo;Misassignment of Role = Game Loss&rdquo;, Reid Duke's
        &ldquo;every card needs a job&rdquo; and &ldquo;Band-Aid&rdquo;
        anti-pattern, PVDDR's potential-vs-realized power framework,
        Frank Karsten's mana-base regression, the Limited Resources
        UBER/CABS doctrine, and a kill-plan-protected sideboarding
        rule. Each rule is gated by an A/B eval harness with a boolean
        criterion grader before it can become the default.{" "}
        <Link
          to="/deck-building-method"
          className="text-accent hover:underline"
        >
          Read the deck-building method →
        </Link>
      </p>
    ),
  },
  {
    q: "Which formats are supported?",
    open: true,
    a: "Standard only, for now. Pioneer and Commander are on the roadmap.",
  },
  {
    q: "Where does the card data come from?",
    a: "Card text is sourced from Scryfall's oracle data, refreshed daily and locked to the legal Standard pool. Every card is embedded into a high-dimensional vector index so the Oracle can retrieve by ability, keyword, or synergy, not just text match. Tournament lists feeding retrieval are cited with the original source URL.",
  },
  {
    q: "Can it actually play the games?",
    a: "Yes. Decks are exported to Forge (the open-source rules-complete MTG engine) and run headless. You get per-game logs, W-L-D outcomes, and detailed natural language summary of the simulated matches.",
  },
  {
    q: "Can the Oracle analyze a deck I already built?",
    a: "Yes. Paste your decklist (Arena, Moxfield, plain text) and the Oracle reviews it: spots weak spots, suggests upgrades backed by tournament data, and walks through sideboarding + gameplay notes for each archetype you're likely to face (Aggro, Midrange, Control, and Combo). Useful whether you're iterating on a homebrew or preparing for an FNM.",
  },
  {
    q: "Is it free?",
    a: `Yes. Every account gets ${PLANS.free.monthly_grant} credits per month, and playtesting itself is always free (sim as many games as you want). That's enough for about ${Math.floor(PLANS.free.monthly_grant / ACTION_COSTS.build_deck)} full deck builds and Q&A on the free tier. Need more? One-time top-ups start at $5 and never expire (no subscription), or the ${PLANS.starter.name} plan is $${PLANS.starter.price_usd}/mo for ${PLANS.starter.monthly_grant.toLocaleString()} credits.`,
  },
  {
    q: "What does each action cost?",
    a: `Running a playtest match is free. Building a deck is ${ACTION_COSTS.build_deck} credits, uploading & analyzing your own list is ${ACTION_COSTS.upload_deck}, an AI match summary is ${ACTION_COSTS.match_summary}, and asking a follow-up question about a deck is ${ACTION_COSTS.ask_deck}. Failed actions are refunded automatically. You only pay for what completes.`,
  },
  {
    q: "How current is the meta data?",
    a: "Card pool refreshes on every new set release. Tournament exemplars refresh weekly from six sources, cross-deduped by mainboard hash, with the highest-tier copy kept.",
  },
];

function Notes({ exemplarCount: _exemplarCount }: { exemplarCount: number }) {
  // _exemplarCount is currently unused inside the static NOTES list, but
  // taking it as a prop keeps the surface symmetric with the other
  // sections and ready for any inline copy that wants the live count.
  return (
    <section className="border-t border-border/60">
      <div className="mx-auto max-w-2xl px-6 py-24">
        <div className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
          Notes
        </div>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
          Q & A
        </h2>
        <dl className="mt-10 divide-y divide-border/60">
          {NOTES.map((f) => (
            <details
              key={f.q}
              open={f.open}
              className="group py-5"
            >
              <summary className="cursor-pointer list-none flex items-baseline justify-between gap-4">
                <span className="font-medium tracking-tight text-fg">
                  {f.q}
                </span>
                <span className="font-mono text-fg-subtle shrink-0 transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <div className="mt-3 text-sm text-fg-muted leading-relaxed">
                {typeof f.a === "string" ? <p>{f.a}</p> : f.a}
              </div>
            </details>
          ))}
        </dl>
      </div>
    </section>
  );
}

// Editorial one-line closer. Headline left-aligned, CTA reads as the last
// clause of the sentence. No centered wrapper card, no subhead.
function CtaFooter() {
  return (
    <section className="border-t border-border/60 bg-bg-surface/30">
      <div className="mx-auto max-w-5xl px-6 py-28">
        <p className="font-display text-4xl md:text-6xl font-medium tracking-[-0.02em] leading-[1.05] max-w-4xl">
          Build a deck{" "}
          <em className="italic font-normal">worth sleeving.</em>
          {" "}
          <SignUpButton mode="modal">
            <LiquidButton className="text-base h-12 ml-2">
              Start your first build →
            </LiquidButton>
          </SignUpButton>
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border/60">
      <div className="mx-auto max-w-6xl px-6 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-fg-subtle">
        <div className="flex items-center gap-2">
          <img src="/logo.svg" alt="" width={18} height={18} aria-hidden />
          <span>© {new Date().getFullYear()} MTG Oracle</span>
        </div>
        <div className="flex items-center gap-4">
          <span>
            Magic: The Gathering is © Wizards of the Coast. Unaffiliated fan project.
          </span>
        </div>
      </div>
    </footer>
  );
}
